// src/components/Services/Services_CyberSecurity.jsx
import React from 'react';
import { motion } from 'framer-motion';
import securityGif from '../images/photos/security.gif'; // ✅ Ensure path

// Page transition animations
const pageVariants = {
  initial: { rotateY: 90, opacity: 0 },
  in: { rotateY: 0, opacity: 1 },
  out: { rotateY: -90, opacity: 0 },
};

const pageTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const fadeUp = {
  hidden: { y: 10, opacity: 0 },
  visible: (i = 0) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.06 * i },
  }),
};

export default function Services_CyberSecurity() {
  const services = [
    'Android Zero Touch Enrollment',
    'Android Management',
    'Cloud Computing',
    'Cloud Management Service',
    'Cyber Security',
    'iOS Management',
    'Mobile Management',
    'IT Management',
    'IT Support',
    'Software Development',
    'Digital Marketing',
  ];

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      style={{
        minHeight: '100vh',
        background: '#040d1fff',
        color: '#e5e9edff',
        padding: '32px 16px',
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* HERO SECTION */}
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 32,
            alignItems: 'center',
          }}
        >
          {/* LEFT - GIF */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <img
              src={securityGif}
              alt="Cybersecurity demo"
              style={{
                width: '100%',
                maxWidth: 500,
                height: 'auto',
                borderRadius: 12,
                boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
              }}
              loading="lazy"
            />
          </motion.div>

          {/* RIGHT - TEXT */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={1}>
            <h1
              style={{
                marginTop: 0,
                fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
                color: '#6dd4e8ff',
              }}
            >
              Cyber Security
            </h1>
            <p
              style={{
                lineHeight: 1.6,
                fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                color: '#e2e8f0',
              }}
            >
              Cybersecurity is the practice of protecting systems, networks, and programs
              from digital attacks. At Zyntro, we offer comprehensive cybersecurity
              services to safeguard your organization against evolving threats and ensure
              compliance with global standards.
            </p>

            <h3
              style={{
                marginTop: 16,
                fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
                color: '#53bdd2ff',
              }}
            >
              Our Cybersecurity Services
            </h3>
            <ul
              style={{
                lineHeight: 1.7,
                fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                color: '#f1f5f9',
                paddingLeft: 20,
              }}
            >
              <li><strong>Network Security</strong> — firewalls, IDS/IPS, and secure network design.</li>
              <li><strong>Application Security</strong> — secure coding, vulnerability testing.</li>
              <li><strong>Endpoint Protection</strong> — antivirus, encryption, mobile security.</li>
              <li><strong>Cloud Security</strong> — securing cloud environments and access.</li>
              <li><strong>Identity & Access Management</strong> — MFA, RBAC, SSO integration.</li>
              <li><strong>Incident Response</strong> — proactive monitoring, detection, and recovery.</li>
              <li><strong>Disaster Recovery</strong> — backup solutions and business continuity.</li>
              <li><strong>Awareness Training</strong> — educating staff to reduce risks.</li>
            </ul>

            {/* Buttons */}
            <div
              style={{
                marginTop: 18,
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <a
                href="mailto:support@zyntro.in"
                style={{
                  background: '#f69f48ff',
                  color: 'white',
                  padding: '10px 16px',
                  borderRadius: 8,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                }}
              >
                Email Support
              </a>
              <a
                href="tel:+919956353236"
                style={{
                  border: '1px solid #4e4e54ff',
                  color: '#010101ff',
                  padding: '10px 16px',
                  borderRadius: 8,
                  textDecoration: 'none',
                  fontWeight: 600,
                  background: 'white',
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                }}
              >
                Call +91-9956353236
              </a>
            </div>
          </motion.div>
        </section>

        {/* OTHER SERVICES */}
        <section style={{ marginTop: 40 }}>
          <h3
            style={{
              color: '#4fe1f4ff',
              fontSize: 'clamp(1.2rem, 3vw, 1.6rem)',
              marginBottom: 16,
            }}
          >
            Other Services
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 14,
            }}
          >
            {services.map((s) => (
              <div
                key={s}
                style={{
                  background: '#1e293b',
                  padding: 12,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: '#1e7d95ff',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    flexShrink: 0,
                  }}
                >
                  {s.charAt(0)}
                </div>
                <span style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>{s}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* RESPONSIVE INLINE MEDIA QUERY */}
      <style>
        {`
          @media (max-width: 900px) {
            section {
              grid-template-columns: 1fr !important;
              text-align: center;
            }
            section img {
              margin: 0 auto;
            }
            section ul {
              text-align: left;
            }
          }
        `}
      </style>
    </motion.div>
  );
}
